import * as stylex from '@stylexjs/stylex';
import { Link } from 'react-router-dom';
import { ModelCard } from '@/components/ModelCard';
import { Sparkles, BookOpen, ArrowRight, Axe, BookOpenCheck, Bot, Video, MessageCircle, Music, Music2, Image as ImageIcon } from 'lucide-react';
// Import the StyleX styles
import { styles } from '@/styles/Homepage.stylex';

export default function HomePage() {
    const additionalFeatures = [
        {
            title: "Explore Examples",
            description: "Explore Full Projects Examples created with AiBrush Studio.",
            icon: <BookOpenCheck size={20} />,
            href: "/examples",
            hrefDoc: "Explore Examples"
        },
        {
            title: "Workspace",
            description: "Explore the workspace and learn how to use it to create any media type.",
            icon: <Axe size={20} />,
            href: "/workspace",
            hrefDoc: "Workspace"
        },
        {
            title: "Explore AI Models",
            description: "Discover all available AI models and their capabilities.",
            icon: <Bot size={20} />,
            href: "/ai-models",
            hrefDoc: "AI Models"
        }
    ];

    return (
        <div {...stylex.props(styles.homepage)}>
            {/* Hero Section */}
            <section {...stylex.props(styles.hero)}>
                <div {...stylex.props(styles.heroContent)}>
                    <h1 {...stylex.props(styles.heroTitle)}>
                        {/* Combining heroTitle_span for layout and highlight for the underline effect */}
                        <span {...stylex.props(styles.heroTitleSpan, styles.highlight)}>Learn.</span>{' '}
                        <span {...stylex.props(styles.heroTitleSpan, styles.highlight)}>Build.</span>{' '}
                        <span {...stylex.props(styles.heroTitleSpan, styles.highlight)}>Master.</span>
                    </h1>
                    <p {...stylex.props(styles.heroSubtitle)}>
                        Your complete guide to AiBrush Studio - from first steps to advanced techniques. Discover AI-powered media creation through comprehensive tutorials, examples, and expert insights.
                    </p>
                    <div {...stylex.props(styles.heroButtons)}>
                        <Link to="/quick-start" {...stylex.props(styles.primaryButton)}>
                            Start Learning <BookOpen size={16} />
                        </Link>
                    </div>
                </div>
                <div {...stylex.props(styles.heroImageContainer)}>
                    <div {...stylex.props(styles.heroImage)}>
                        <div {...stylex.props(styles.gridPattern)}></div>
                        <div {...stylex.props(styles.imageOverlay)}>
                            <Sparkles size={80} {...stylex.props(styles.sparkleIcon)} />
                        </div>
                    </div>
                </div>
            </section>

            {/* Featured AI Models */}
            <section {...stylex.props(styles.featuredModels)}>
                <h2 {...stylex.props(styles.sectionTitle)}>AI Models</h2>
                <div {...stylex.props(styles.modelsGrid)}>
                    <ModelCard
                        title="Image Generation"
                        description="Create stunning images from text descriptions"
                        category="Visual"
                        href="/ai-models/#Image-Models"
                        icon={<ImageIcon size={24} />}
                        style={[styles.modelCard, styles.imageModels, styles.stagger1]}
                        features={['Style Presets', 'High Resolution', 'Prompt Engineering']}
                    />
                    <ModelCard
                        title="Video Generation"
                        description="Generate videos from text prompts and image sequences"
                        category="Visual"
                        href="/ai-models/#Video-Models"
                        icon={<Video size={24} />}
                        style={[styles.modelCard, styles.videoModels, styles.stagger2]}
                        features={['AI Animation', 'Style Transfer', 'Custom Length']}
                    />
                    <ModelCard
                        title="Speech Generation"
                        description="Convert text to natural-sounding speech with multiple voices"
                        category="Audio"
                        href="/ai-models/#Speech-Models"
                        icon={<MessageCircle size={24} />}
                        style={[styles.modelCard, styles.speechModels, styles.stagger3]}
                        features={['50+ Languages', 'Multiple Voices', 'Emotion Control']}
                    />
                    <ModelCard
                        title="Music Generation"
                        description="Compose original music tracks with AI-powered composition tools"
                        category="Audio"
                        href="/ai-models/#Music-Models"
                        icon={<Music size={24} />}
                        style={[styles.modelCard, styles.musicModels, styles.stagger4]}
                        features={['Multiple Genres', 'Custom Length', 'High Quality']}
                    />
                    <ModelCard
                        title="Sound Effect Generation"
                        description="Create immersive audio effects and ambient sounds for your projects"
                        category="Audio"
                        href="/ai-models/#Sound-Effects-Models"
                        icon={<Music2 size={24} />}
                        style={[styles.modelCard, styles.soundEffectsModels, styles.stagger1]}
                        features={['Various Sound Types', 'Custom Duration', 'High Quality Audio']}
                    />
                    <ModelCard
                        title="Chat with AI"
                        description="Interact with advanced AI models for creative assistance"
                        category="AI Chat"
                        href="/ai-models/#Chat-Models"
                        icon={<Bot size={24} />}
                        style={[styles.modelCard, styles.chatModels, styles.stagger2]}
                        features={['Multiple AI Models', 'Context Awareness', 'Creative Support']}
                    />
                </div>
            </section>

            {/* Quick Start Guides */}
            <section>
                <h2 {...stylex.props(styles.sectionTitle)}>Quick Start Guides</h2>
                <div {...stylex.props(styles.guidesGrid)}>
                    {additionalFeatures.map((feature, index) => {
                        // Apply stagger based on index (stagger1, stagger2, stagger3)
                        const staggerKey = `stagger${(index % 3) + 1}` as 'stagger1' | 'stagger2' | 'stagger3';

                        return (
                            <div key={index} {...stylex.props(styles.guideCard, styles[staggerKey])}>
                                <h3 {...stylex.props(styles.guideCardTitle)}>{feature.title}</h3>
                                <p {...stylex.props(styles.guideCardText)}>{feature.description}</p>
                                <a href={feature.href} {...stylex.props(styles.guideLink)}>
                                    {feature.hrefDoc} <ArrowRight size={16} />
                                </a>
                            </div>
                        );
                    })}
                </div>
            </section>
        </div>
    );
}
// Force recompile